import {
    ActionReducerMapBuilder,
    createAsyncThunk,
    createSlice,
    Draft,
    SerializedError,
} from '@reduxjs/toolkit';
import { urlParamReplicaId, urlProjectDocStatus } from '../../Constants';
import { IAction, SlicePropsWithData } from '../store';
import { apiFetch } from '../../util/ProjectUtils';

/**
 * Initial slice state value.
 */
const initialState: IProjectDocStatusState = {
    data: [],
    loading: false,
    error: null,
};

/**
 * Collection of the reducers for this slice.
 */
const reducers = {};

/**
 * Collection of the thunks for this slice, along with their reducer functions.
 */
const thunks = {
    /**
     * Async function that fetches the available document statuses for a given project database.
     * @param replicaId - ReplicaId for the target project database.
     * @returns Promise for the HTTP request.
     */
    getProjectDocStatus: async (replicaId: string): Promise<IProjectDocStatus[]> => {
        try {
            const url = urlProjectDocStatus.replace(urlParamReplicaId, replicaId);
            const response = await apiFetch(url);

            return response.ok
                ? Promise.resolve(await response.json())
                : Promise.reject(new Error(await response.text()));
        } catch (e) {
            return Promise.reject(e as Error);
        }
    },

    /**
     * Registers the 'extraReducers' functions for the slice's thunks.
     */
    _reducers: (builder: ActionReducerMapBuilder<IProjectDocStatusState>) => {
        builder.addCase(getProjectDocStatus.pending, (state: Draft<IProjectDocStatusState>) => {
            state.data = null;
            state.loading = true;
            state.error = null;
        });
        builder.addCase(
            getProjectDocStatus.fulfilled,
            (state: Draft<IProjectDocStatusState>, action: IAction<IProjectDocStatus[]>) => {
                state.loading = false;
                state.error = null;
                state.data = action.payload;
            },
        );
        builder.addCase(
            getProjectDocStatus.rejected,
            (state: Draft<IProjectDocStatusState>, action: IAction<SerializedError>) => {
                state.loading = false;
                state.error = action.error;
                state.data = null;
            },
        );
    },
};

/**
 * Expose thunk functions as exports.
 */
export const getProjectDocStatus = createAsyncThunk(
    'project/docStatus',
    thunks.getProjectDocStatus,
);

/**
 * Interface that describes the state object for this redux slice.
 */
export type IProjectDocStatusState = SlicePropsWithData<IProjectDocStatus[]>;

/**
 * Interface that describes a DocStatus configuration.
 */
export interface IProjectDocStatus {
    /** DocStatus name. */
    name: string;

    /** DocStatus SVG Icon. */
    icon: string;

    /** DocStatus icon color. */
    color: string;

    /** DocStatus Display text translations */
    translations: { [id: string]: string };
}

/**
 * Create the redux slice.
 */
export const projectDocStatusSlice = createSlice({
    name: 'projectDocStatus',
    extraReducers: thunks._reducers,
    initialState,
    reducers,
});

/**
 * Export slice reducer as default.
 */
export default projectDocStatusSlice.reducer;
